import { Wifi, WifiOff } from 'lucide-react';
import { useEffect, useEffectEvent, useState } from 'react';
import { LEAVE_MS } from '@/components/brand/threshold';
import { Button, cn } from '@/components/ui';

/*
  ConnectionStrip — нет связи с сервером (R42, spec-2 §3.9): строка под полосой вкладок основного окна
  (WorkspaceCanvas notice). Вкладки и данные остаются как были — строка говорит, на какое время они и когда
  следующая попытка: 2, 4, 8, 16 с, дальше раз в 30 с. «Повторить сейчас» — попытка сразу, крутилка в кнопке
  (короче секунды, R32). Связь вернулась — строка 1,6 с держится в тоне «готово», «Связь восстановлена», и
  гаснет; тоста нет. Знак в меню о связи ничего не показывает (решение 21.10).

  Строку держит сама: вызывающий передаёт только offline, а «восстановлена» доигрывает она. Голос — один раз
  на смену: обратный отсчёт читалке не зачитывается. Область голоса стоит всегда, пустая, пока связь есть:
  читалка объявляет перемены в уже существующей области, а вставленную вместе с текстом может пропустить —
  как раз «Нет связи».
  Настоящее обнаружение потери связи и повторы — зона ядра (spec-2 §11); здесь попытка зовёт onRetry.
*/

interface ConnectionStripProps {
  /** Связи нет. Стал false — строка показывает «Связь восстановлена» и уходит. */
  offline: boolean;
  /** На какое время данные на экране: «данные на 14:32». */
  dataAt: string;
  /** Попытка связи — по расписанию и по «Повторить сейчас». */
  onRetry?: () => void;
}

/** Через сколько секунд следующая попытка: по порядку, дальше — последнее. */
const RETRY_AFTER_S = [2, 4, 8, 16, 30] as const;
/** Сколько идёт попытка. */
const TRY_MS = 600;
/** Сколько держится «Связь восстановлена». */
const RESTORED_MS = 1600;

type Phase = 'off' | 'offline' | 'restored' | 'leaving';

interface Retry {
  /** Сколько попыток уже было. */
  n: number;
  /** Секунд до следующей. */
  left: number;
  trying: boolean;
}

const FIRST_RETRY: Retry = { n: 0, left: RETRY_AFTER_S[0], trying: false };

export function ConnectionStrip({ offline, dataAt, onRetry }: ConnectionStripProps) {
  const [phase, setPhase] = useState<Phase>(offline ? 'offline' : 'off');
  const [retry, setRetry] = useState<Retry>(FIRST_RETRY);
  const [was, setWas] = useState(offline);
  if (was !== offline) {
    setWas(offline);
    if (offline) {
      setPhase('offline');
      setRetry(FIRST_RETRY);
    } else if (phase === 'offline') setPhase('restored');
  }

  // Обратный отсчёт — тиком раз в секунду; дошёл до нуля — попытка.
  const ticking = phase === 'offline' && !retry.trying;
  useEffect(() => {
    if (!ticking) return undefined;
    const t = window.setInterval(
      () => setRetry((r) => (r.left > 1 ? { ...r, left: r.left - 1 } : { ...r, left: 0, trying: true })),
      1000,
    );
    return () => window.clearInterval(t);
  }, [ticking]);

  // Попытка: не удалась (связи всё ещё нет) — следующая позже.
  const attempt = useEffectEvent(() => onRetry?.());
  useEffect(() => {
    if (!retry.trying) return undefined;
    attempt();
    const t = window.setTimeout(
      () =>
        setRetry((r) => ({
          n: r.n + 1,
          left: RETRY_AFTER_S[Math.min(r.n + 1, RETRY_AFTER_S.length - 1)],
          trying: false,
        })),
      TRY_MS,
    );
    return () => window.clearTimeout(t);
  }, [retry.trying]);

  // «Связь восстановлена» держится и гаснет.
  useEffect(() => {
    if (phase !== 'restored' && phase !== 'leaving') return undefined;
    const t = window.setTimeout(
      () => setPhase((p) => (p === 'restored' ? 'leaving' : p === 'leaving' ? 'off' : p)),
      phase === 'restored' ? RESTORED_MS : LEAVE_MS,
    );
    return () => window.clearTimeout(t);
  }, [phase]);

  const on = phase !== 'off';
  const restored = phase !== 'offline';
  return (
    <div
      role="status"
      aria-live="polite"
      data-slot="connection-strip"
      className={cn(
        on && 'flex h-9 shrink-0 items-center gap-2 border-b px-4 text-sm',
        on &&
          (restored
            ? 'border-success-line bg-success-soft text-success-strong'
            : 'border-warning-line bg-warning-soft text-warning-strong'),
        on && (phase === 'leaving' ? 'animate-fade-out' : 'animate-fade-in'),
      )}
    >
      {on && (
        <>
          {restored ? (
            <Wifi aria-hidden className="size-4 shrink-0" />
          ) : (
            <WifiOff aria-hidden className="size-4 shrink-0" />
          )}
          <span className="min-w-0 flex-1 truncate tabular-nums">
            {restored ? (
              'Связь восстановлена'
            ) : (
              <>
                Нет связи с сервером · данные на {dataAt}
                <span aria-hidden>
                  {retry.trying ? ' · проверяем связь' : ` · повтор через ${retry.left} с`}
                </span>
              </>
            )}
          </span>
          {!restored && (
            <Button
              size="sm"
              variant="ghost"
              className="shrink-0"
              loading={retry.trying}
              onClick={() => setRetry((r) => ({ ...r, left: 0, trying: true }))}
            >
              Повторить сейчас
            </Button>
          )}
        </>
      )}
    </div>
  );
}
